import type { estypes } from '@elastic/elasticsearch'

export interface Filter {
  key: string
  count: number
}

interface SearchResponse {
  hits: estypes.SearchHit[]
  total: number
}

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message
  return String(error)
}

export const useSearch = async ({
  keywords,
  filter,
  filterReset,
}: {
  keywords: Ref<string>
  filter: Ref<string[]>
  filterReset: () => void
}) => {
  const config = useRuntimeConfig()
  const route = useRoute()
  const { languageCode } = useLocale()

  const perPage = ref(6)
  const page = ref(route.query.page ? +route.query.page : 1)
  const result = reactive({
    loading: false,
    success: false,
    error: false as boolean | string,
    total: 0,
    items: [] as estypes.SearchHit[],
    filters: [] as Filter[],
  })
  const pages = computed(() => Math.ceil(result.total / perPage.value))

  const controller = ref<AbortController | null>(null)
  const search = async () => {
    if (controller.value) {
      controller.value.abort()
    }

    result.loading = true
    controller.value = new AbortController()

    try {
      const payload = {
        langcode: languageCode.value,
        text: keywords.value,
        types: filter.value.join(','),
        from: perPage.value * (page.value - 1),
        size: perPage.value,
      }

      const response: SearchResponse = await fetch(`${config.public.frontendBaseUrl}/api/elasticsearch/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: controller.value.signal,
      }).then((response) => response.json())

      result.success = true
      result.items = response.hits
      result.total = response.total
    } catch (error: unknown) {
      result.error = getErrorMessage(error)
      result.success = false
    }
    result.loading = false
  }

  const filters = async () => {
    const response: Filter[] = await fetch(`${config.public.frontendBaseUrl}/api/elasticsearch/search-filters`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        langcode: languageCode.value,
        text: keywords.value,
        from: perPage.value * (page.value - 1),
        size: perPage.value,
      }),
    }).then((response) => response.json())

    let filters = [...(response ?? [])]

    const webcontent = { key: 'webcontent', count: 0 }
    filters.forEach((filter) => {
      if (filter.key === 'page') {
        webcontent.count += filter.count
      }
      if (filter.key === 'landing_page') {
        webcontent.count += filter.count
      }
    })
    if (webcontent.count) filters.push(webcontent)

    filters = filters.filter((filter) => !['page', 'landing_page'].includes(filter.key))

    result.filters = [
      {
        key: 'all',
        count: response.reduce((acc: number, filter: Filter) => acc + filter.count, 0),
      },
      ...filters,
    ]
  }

  watch(keywords, async (nextKeywords, previousKeywords) => {
    if ([1, 2].includes(nextKeywords.length)) return

    await filters()

    if (nextKeywords !== previousKeywords && page.value !== 1) return (page.value = 1) // triggers a new watch callback
    if (nextKeywords !== previousKeywords && !filter.value.includes('all')) return (filter.value = ['all'])

    await search()
  })

  watch(page, async () => {
    await search()
  })

  watch(filter, async (newFilter, oldFilter) => {
    if (newFilter.includes('all') && !oldFilter.includes('all')) return (filter.value = ['all'])

    if (newFilter.length > 1 && newFilter.includes('all'))
      return (filter.value = filter.value.filter((filter) => filter !== 'all'))

    if (newFilter.length === 0) return (filter.value = ['all'])

    if (page.value !== 1) return (page.value = 1) // triggers a new watch callback

    await search()
  })

  const reset = () => {
    page.value = 1
    filterReset()
  }

  return {
    search,
    filters,
    result,
    page,
    pages,
    keywords,
    reset,
  } as const
}
